/**
 * Firebase data types and interfaces
 * Matches the exact structure used by the mobile app for compatibility
 */

import { Timestamp } from 'firebase/firestore';

// User interface matching mobile app structure
export interface User {
  ownerID: string;
  masterOwnerID: string;
  name: string;
  email: string;
  role: 'Admin' | 'Tech' | 'Clerk';
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  expoPushToken?: string;
  assignedSites?: string[]; // For Clerk role
  /** Organization default sweep team # (admin); user can override per device in Sweeps. */
  defaultSweepTeamNumber?: number | null;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
}

// Site interface matching mobile app structure
export interface Site {
  id: string;
  name: string;
  address: string;
  coordinates: {
    latitude: number;
    longitude: number;
    latitudeDelta: number;
    longitudeDelta: number;
  };
  masterOwnerID: string;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
}

// Device interface matching mobile app structure with backward compatibility
export interface Device {
  id?: string;
  qrCodeNumber?: number; // Numeric QR code identifier
  deviceType: string; // e.g., "ATM", "Pump Station"
  modelNumber?: string; // Reader model number
  modelName: string; // Reader model name
  specificLocation?: string; // Optional specific location within site
  tagType?: string; // From mobile Report readerDetails.device
  coordinates?: {
    latitude: number;
    longitude: number;
    latitudeDelta?: number;
    longitudeDelta?: number;
  };
  qrCodeUrl?: string; // Firebase Storage URL for QR code image
  siteId?: string; // Reference to parent site (implicit from subcollection)
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
  
  // Legacy fields for backward compatibility with existing web app data
  serialNumber?: string; // For backward compatibility
  batteryLevel?: number; // Hardware status info
  signalStrength?: number; // Hardware status info
  firmwareVersion?: string; // Hardware version info
  lastSeen?: Date | Timestamp; // Last communication time
  location?: { // Legacy location format
    description: string;
    coordinates?: {
      x: number;
      y: number;
    };
  };
  configuration?: { // Legacy configuration format
    scanInterval: number;
    sensitivity: 'low' | 'medium' | 'high';
    autoUpdate: boolean;
    alertsEnabled: boolean;
  };
  status?: 'online' | 'offline' | 'maintenance' | 'error'; // Legacy status
}

// ShareData and LawEnforcement matching mobile app (Report.ts)
export interface ReportShareData {
  images?: {
    uris?: Array<{ id: string; img: string }>;
    privacy?: 'public' | 'private';
  };
  feedback?: {
    value?: string;
    privacy?: 'public' | 'private';
  };
  additionalNotes?: string;
}

export interface LawEnforcementDetails {
  reported: boolean;
  department?: string;
  contactName?: string;
  contactPhone?: string;
  caseNumber?: string;
  reportedAt?: Timestamp;
}

// Report interface matching mobile app structure (Report.ts + ReportDetailsNew)
export interface Report {
  id: string;
  status: 'No Skimmer Detected' | 'Skimmer Detected' | 'Skimmer Detected!' | 'Proccessing Data'; // mobile uses 'Skimmer Detected'
  user: {
    ownerID: string;
    masterOwnerID: string;
    name: string;
    email: string;
    role: string;
  };
  location: {
    latitude?: number;
    longitude?: number;
    address: string;
    assignedUsers: AssignedUser[];
  };
  privacy: 'public' | 'private';
  readerDetails: {
    siteId: string | null; // null for manual reports
    siteName: string;
    device: Device;
  };
  message?: string; // Optional message/notes
  shareData?: ReportShareData;
  sitesDetails?: {
    id?: string;
    name?: string;
    address?: string;
    latitude?: number;
    longitude?: number;
    photo?: string;
    [key: string]: any;
  };
  createdAt: Timestamp | Date;
  createdAtTzOffsetMinutes?: number;
  createdAtTimeZoneLabel?: string;
  updatedAt?: Timestamp | Date;
  isManualEntry?: boolean;
  caseNumber?: string;
  lawEnforcement?: LawEnforcementDetails;
  confirmedSkimmer?: boolean;
  confirmedSkimmerAt?: Timestamp | null;
  confirmedSkimmerBy?: { ownerID: string; name: string; email: string } | null;
  lastEditBy?: {
    name: string;
    email: string;
    role: string;
    createdAt: Timestamp | Date;
  };
  severity?: string;
  actionsTaken?: string[];
  actionsTakenNotes?: string;
  /** Device scan coil samples (same field as mobile); may be array or map-like from Firestore. */
  coilShiftData?: unknown;
}

// Assigned user interface for reports
export interface AssignedUser {
  ownerID: string;
  name: string;
  email: string;
  role: string;
}

// Profile interface for company information
export interface Profile {
  id: string;
  companyName: string;
  industry?: string; // Added from mobile app
  department?: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  logo?: string; // Firebase Storage URL
  email?: string;
  phone?: string;
  address?: string;
  website?: string;
  taxId?: string;
  createdBy?: string; // Added from mobile app - full name of creator
  masterOwnerID: string;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
}

// Collection references matching mobile app Firebase structure
export const COLLECTIONS = {
  USERS: 'users',
  SITES: 'sites',
  REPORTS: 'reports',
  PROFILES: 'profiles',
  COMPANIES: 'companies',
} as const;

// Helper functions for device subcollection paths
export const getDevicesPath = (siteId: string) => `sites/${siteId}/devices`;

// Type guards and validation functions
export const isValidReport = (data: any): data is Report => {
  return (
    data &&
    typeof data.status === 'string' &&
    data.user &&
    typeof data.user.ownerID === 'string' &&
    typeof data.user.masterOwnerID === 'string' &&
    data.location &&
    data.readerDetails &&
    data.readerDetails.siteId &&
    data.readerDetails.device
  );
};

export const isValidDevice = (data: any): data is Device => {
  return (
    data &&
    typeof data.qrCodeNumber === 'number' &&
    typeof data.deviceType === 'string' &&
    typeof data.modelNumber === 'string' &&
    typeof data.modelName === 'string' &&
    data.coordinates &&
    typeof data.coordinates.latitude === 'number' &&
    typeof data.coordinates.longitude === 'number'
  );
};

export const isValidSite = (data: any): data is Site => {
  return (
    data &&
    typeof data.name === 'string' &&
    typeof data.address === 'string' &&
    data.coordinates &&
    typeof data.coordinates.latitude === 'number' &&
    typeof data.coordinates.longitude === 'number' &&
    typeof data.masterOwnerID === 'string'
  );
};

// Firebase timestamp conversion helpers
export const convertTimestamp = (timestamp: Timestamp | Date | any): Date => {
  if (timestamp == null || timestamp === '') return new Date();

  if (timestamp instanceof Date) {
    return timestamp;
  }

  if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    const d = new Date(timestamp);
    return isNaN(d.getTime()) ? new Date() : d;
  }

  if (typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }

  if (typeof timestamp.seconds === 'number') {
    return new Date(timestamp.seconds * 1000 + (typeof timestamp.nanoseconds === 'number' ? timestamp.nanoseconds / 1e6 : 0));
  }

  return new Date();
};

// Generate numeric QR code number (matching mobile app logic)
export const generateQrCodeNumber = (): number => {
  const timestamp = Date.now();
  const randomSuffix = Math.floor(Math.random() * 10000);
  const paddedSuffix = randomSuffix.toString().padStart(4, '0');
  return parseInt(`${timestamp}${paddedSuffix}`);
};

// Report privacy and permission helpers
export const canUserViewReport = (
  report: Report, 
  user: User
): boolean => {
  // Public reports are visible to everyone
  if (report.privacy === 'public') {
    return true;
  }
  
  // Private reports visibility rules based on mobile app logic
  if (report.privacy === 'private') {
    // Clerk role can only see reports from same masterOwnerID and ownerID
    if (user.role === 'Clerk') {
      return (
        report.user.masterOwnerID === user.masterOwnerID &&
        report.user.ownerID === user.ownerID
      );
    } else {
      // Admin and Tech can see all reports from same masterOwnerID
      return report.user.masterOwnerID === user.masterOwnerID;
    }
  }
  
  return false;
};

// Check if user can edit or delete a report
export const canUserEditReport = (
  report: Report, 
  user: User
): boolean => {
  // Only Admin and Tech users can edit reports
  if (user.role !== 'Admin' && user.role !== 'Tech') {
    return false;
  }
  
  // For private reports, user must be from same masterOwnerID
  if (report.privacy === 'private') {
    return report.user.masterOwnerID === user.masterOwnerID;
  }
  
  // For public reports, only admin users from the same company can edit/delete
  if (report.privacy === 'public') {
    return user.role === 'Admin' && report.user.masterOwnerID === user.masterOwnerID;
  }
  
  return false;
};

// Default coordinate deltas for map regions (matching mobile app)
export const DEFAULT_COORDINATE_DELTAS = {
  latitudeDelta: 0.0922,
  longitudeDelta: 0.0421,
} as const;

// Status colors matching mobile app theme
export const getReportStatusColor = (status: Report['status']): string => {
  switch (status) {
    case 'No Skimmer Detected':
      return '#53EA45'; // Green
    case 'Skimmer Detected!':
      return '#5A0000'; // Dark Red
    case 'Proccessing Data':
      return '#862FFF'; // Purple
    default:
      return '#2FB8FF'; // Blue
  }
};
